import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, ArrowLeft, Eye, EyeOff, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  verifyAdminToken,
  getBlogPostsAdmin,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost
} from '../services/api';

const categories = ['Loan Guide', 'Investing Tips', 'Market Insights', 'Case Studies'];

const AdminBlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', category: 'Loan Guide',
    coverImage: '', author: 'Close Now Lending', tags: '', readTime: 5, isPublished: true
  });

  const checkAuth = useCallback(async () => {
    try {
      await verifyAdminToken();
    } catch {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBlogPostsAdmin();
      setPosts(data);
    } catch (err) {
      console.error('Failed to load blog posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchPosts();
  }, [checkAuth, fetchPosts]);

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const resetForm = () => {
    setFormData({
      title: '', slug: '', excerpt: '', content: '', category: 'Loan Guide',
      coverImage: '', author: 'Close Now Lending', tags: '', readTime: 5, isPublished: true
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: '', // Will be fetched if needed; for listing we may not have full content
      category: post.category,
      coverImage: post.coverImage,
      author: post.author,
      tags: post.tags.join(', '),
      readTime: post.readTime,
      isPublished: true
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        readTime: parseInt(formData.readTime) || 5
      };

      if (editingPost) {
        await updateBlogPost(editingPost.id, payload);
      } else {
        await createBlogPost(payload);
      }
      resetForm();
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await deleteBlogPost(id);
      fetchPosts();
    } catch (err) {
      alert('Failed to delete blog post');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">Blog Management</h1>
            </div>
          </div>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="admin-blog-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-8" data-testid="admin-blog-form">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{editingPost ? 'Edit Post' : 'New Blog Post'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Title *</Label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: editingPost ? formData.slug : generateSlug(e.target.value) })}
                    data-testid="admin-blog-title"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Slug *</Label>
                  <Input
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    data-testid="admin-blog-slug"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Excerpt *</Label>
                <textarea
                  required
                  rows={2}
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="admin-blog-excerpt"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Content (Markdown) *</Label>
                <textarea
                  required={!editingPost}
                  rows={12}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  placeholder="Write your article in Markdown format..."
                  data-testid="admin-blog-content"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="admin-blog-category"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Cover Image URL</Label>
                  <Input
                    value={formData.coverImage}
                    onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                    placeholder="https://..."
                    data-testid="admin-blog-cover"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Read Time (min)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.readTime}
                    onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                    data-testid="admin-blog-readtime"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Tags (comma-separated)</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="DSCR, Investment, Guide"
                  data-testid="admin-blog-tags"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-blog-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingPost ? 'Update Post' : 'Publish Post'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No blog posts yet. Create your first article!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-blog-list">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg border p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                {post.coverImage && (
                  <img src={post.coverImage} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{post.category}</span>
                    <span>{post.readTime} min read</span>
                    <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="View">
                    <Eye className="h-4 w-4" />
                  </a>
                  <button onClick={() => handleEdit(post)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(post.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogPage;
